import { useEffect, useRef, useState, ReactNode } from "react";

interface LazyMountProps {
  children: ReactNode;
  /** Distance from viewport (in px) at which to mount. Default 300px. */
  rootMargin?: string;
  /** Approximate height to reserve before mount so layout doesn't jump. */
  placeholderMinHeight?: number;
  className?: string;
}

/**
 * Defers rendering its children until the wrapper scrolls near the viewport.
 * Used to keep the initial dashboard paint light when many sections stack vertically.
 */
export function LazyMount({
  children,
  rootMargin = "300px",
  placeholderMinHeight = 120,
  className,
}: LazyMountProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (shown) return;
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShown(true);
          obs.disconnect();
        }
      },
      { rootMargin }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [shown, rootMargin]);

  return (
    <div
      ref={ref}
      className={className}
      style={shown ? undefined : { minHeight: placeholderMinHeight }}
    >
      {shown ? children : null}
    </div>
  );
}
